import { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../config/apiBase';

const ACTION_MAP = {
  View: 'CanView',
  Edit: 'CanEdit',
  Import: 'CanImport',
  Export: 'CanExport',
  Delete: 'CanDelete',
  SaveReport: 'CanSaveReport'
};

const AUTH_SNAPSHOT_KEY = 'mdv.authSnapshot';

function readAuthSnapshot() {
  try {
    const raw = localStorage.getItem(AUTH_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.user) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeAuthSnapshot(snapshot) {
  try {
    localStorage.setItem(AUTH_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    /* quota / private mode */
  }
}

function clearAuthSnapshot() {
  try {
    localStorage.removeItem(AUTH_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
}

function isUnreachableAuthError(error) {
  if (!error) return false;
  if (error.response) {
    const status = error.response.status;
    // Real "not logged in" is 401. Anything else (404/403/5xx/SW 503) is not a logout.
    return status !== 401;
  }
  return (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNREFUSED' ||
    error.code === 'ECONNABORTED' ||
    error.code === 'ERR_HTTP2_PROTOCOL_ERROR' ||
    error.message?.includes('ERR_HTTP2_PROTOCOL_ERROR') ||
    error.message?.includes('Network Error')
  );
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [bootSnapshot] = useState(readAuthSnapshot);
  const [user, setUser] = useState(bootSnapshot?.user ?? null);
  const [permissions, setPermissions] = useState(bootSnapshot?.permissions || {});
  const [permissionsById, setPermissionsById] = useState(bootSnapshot?.permissionsById || {});
  const [userRoles, setUserRoles] = useState(bootSnapshot?.userRoles || []);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(!!bootSnapshot?.user);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(
    !!bootSnapshot?.user?.requiresPasswordChange
  );

  // Configure axios to include credentials for session cookies
  axios.defaults.withCredentials = true;
  // Set default timeout to 30 seconds (30000ms) to prevent hanging requests
  axios.defaults.timeout = 30000;

  const persistSnapshot = (nextUser, permPayload) => {
    if (!nextUser) {
      clearAuthSnapshot();
      return;
    }
    writeAuthSnapshot({
      user: nextUser,
      permissions: permPayload?.permissions || {},
      permissionsById: permPayload?.permissionsById || {},
      userRoles: permPayload?.userRoles || [],
      savedAt: Date.now()
    });
  };

  const applySnapshot = (snapshot) => {
    if (!snapshot?.user) return false;
    setUser(snapshot.user);
    setAuthenticated(true);
    setRequiresPasswordChange(!!snapshot.user?.requiresPasswordChange);
    setPermissions(snapshot.permissions || {});
    setPermissionsById(snapshot.permissionsById || {});
    setUserRoles(snapshot.userRoles || []);
    return true;
  };

  const clearAuthState = () => {
    clearAuthSnapshot();
    setUser(null);
    setAuthenticated(false);
    setRequiresPasswordChange(false);
    setPermissions({});
    setPermissionsById({});
    setUserRoles([]);
  };

  const keepLastKnownSession = (reason) => {
    const snapshot = readAuthSnapshot();
    if (snapshot?.user) {
      console.warn(`[AuthContext] ${reason} — keeping last known session`);
      applySnapshot(snapshot);
      return true;
    }
    clearAuthState();
    return false;
  };

  // Check authentication status on mount, and again when the network returns
  useEffect(() => {
    checkAuth();
    const onOnline = () => {
      checkAuth();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- verify once on mount / reconnect
  }, []);

  const loadPermissions = async () => {
    try {
      const url = `${getApiBaseUrl()}/get_permissions.php`;
      const response = await axios.get(url, {
        withCredentials: true,
        timeout: 30000
      });

      if (response.data.success) {
        const next = {
          permissions: response.data.permissions || {},
          permissionsById: response.data.permissionsById || {},
          userRoles: response.data.roles || []
        };
        setPermissions(next.permissions);
        setPermissionsById(next.permissionsById);
        setUserRoles(next.userRoles);
        return next;
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
      const snapshot = readAuthSnapshot();
      if (snapshot?.permissions) {
        setPermissions(snapshot.permissions);
        setPermissionsById(snapshot.permissionsById || {});
        setUserRoles(snapshot.userRoles || []);
        return {
          permissions: snapshot.permissions,
          permissionsById: snapshot.permissionsById || {},
          userRoles: snapshot.userRoles || []
        };
      }
    }
    return null;
  };

  const checkAuth = async () => {
    try {
      const url = `${getApiBaseUrl()}/check_auth.php`;
      console.log('[AuthContext] Checking auth at:', url);
      const response = await axios.get(url, {
        withCredentials: true,
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('[AuthContext] Auth check response:', response.data);
      
      if (response.data.success && response.data.authenticated) {
        setUser(response.data.user);
        setAuthenticated(true);
        setRequiresPasswordChange(!!response.data.user?.requiresPasswordChange);
        const perms = await loadPermissions();
        persistSnapshot(response.data.user, perms || readAuthSnapshot());
        return true;
      } else {
        // Server confirmed there is no session
        clearAuthState();
        return false;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.warn('[AuthContext] Auth check returned 401 - not authenticated');
        clearAuthState();
        return false;
      }

      if (error.response && (error.response.status === 404 || error.response.status === 403)) {
        console.warn(
          '[AuthContext] /server/check_auth.php not reachable (HTTP ' +
            error.response.status +
            '). Copy the repo server\\ folder into the IIS site\\server\\ and ensure PHP is enabled for *.php.'
        );
      } else if (error.code === 'ERR_HTTP2_PROTOCOL_ERROR' || error.message?.includes('ERR_HTTP2_PROTOCOL_ERROR')) {
        console.error('[AuthContext] HTTP2 protocol error - this may be a server configuration issue:', error.message);
      } else if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED' || !error.response) {
        console.error('[AuthContext] Network error during auth check:', error.message || error.code);
        console.warn('[AuthContext] Server may be unreachable or there is a network issue');
      } else {
        console.error('[AuthContext] Auth check failed:', error);
      }

      if (isUnreachableAuthError(error)) {
        return keepLastKnownSession('Auth server unreachable');
      }

      clearAuthState();
      return false;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const url = `${getApiBaseUrl()}/login.php`;
      const response = await axios.post(url, {
        email,
        password
      }, {
        withCredentials: true,
        timeout: 30000 // 30 second timeout
      });

      if (response.data.success) {
        setUser(response.data.user);
        setAuthenticated(true);
        setRequiresPasswordChange(!!response.data.user?.requiresPasswordChange);
        const perms = await loadPermissions();
        persistSnapshot(response.data.user, perms);
        return { success: true, requiresPasswordChange: !!response.data.user?.requiresPasswordChange };
      } else {
        return { success: false, error: response.data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Detect network errors (no response from server)
      let errorMessage = 'Login failed. Please try again.';
      
      // Check for network errors (no response object means network issue)
      if (!error.response) {
        // Network error - no response from server
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorMessage = 'Connection timeout. Please check your internet connection and try again.';
        } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (error.code === 'ECONNREFUSED') {
          errorMessage = 'Unable to connect to server. Please try again later.';
        } else if (error.message) {
          // Generic network error
          errorMessage = `Connection error: ${error.message}. Please check your internet connection and try again.`;
        } else {
          errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
        }
      } else if (error.response) {
        // Server responded with an error status
        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            // If data is a string, try to parse it
            try {
              const parsed = JSON.parse(error.response.data);
              errorMessage = parsed.error || errorMessage;
            } catch (e) {
              errorMessage = error.response.data || errorMessage;
            }
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.response.status === 0) {
          // Status 0 usually indicates a network/CORS issue
          errorMessage = 'Network error. Please check your internet connection and try again.';
        }
      } else if (error.message) {
        // Fallback to error message if available
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      const url = `${getApiBaseUrl()}/logout.php`;
      await axios.post(url, {}, {
        withCredentials: true,
        timeout: 30000 // 30 second timeout
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthState();
    }
  };

  const hasPermission = (resourceName, action) => {
    if (!authenticated || !resourceName) {
      return false;
    }

    const normalize = (name) => String(name || '').toLowerCase().replace(/\s+/g, '');
    const wanted = normalize(resourceName);
    const resourcePerms =
      permissions[resourceName] ||
      Object.values(permissions).find((p) => normalize(p?.ResourceName) === wanted);

    if (!resourcePerms) return false;
    const permKey = ACTION_MAP[action] || action;
    return resourcePerms[permKey] === 1;
  };

  const hasPermissionByResourceId = (resourceId, action) => {
    if (!authenticated || resourceId == null) {
      return false;
    }

    const resourcePerms = permissionsById[String(resourceId)];
    if (!resourcePerms) {
      return false;
    }

    const permKey = ACTION_MAP[action] || action;
    return resourcePerms[permKey] === 1;
  };

  const value = {
    user,
    permissions,
    permissionsById,
    userRoles,
    authenticated,
    loading,
    login,
    logout,
    checkAuth,
    hasPermission,
    hasPermissionByResourceId,
    loadPermissions,
    requiresPasswordChange,
    setRequiresPasswordChange
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
